import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CreateDebtRequest, Debt, UpdateDebtRequest } from '../types';
import { apiClient } from '../services/api';
import { restorePaginatedQueries, snapshotPaginatedQueries, updatePaginatedQueries, PaginatedSnapshot } from '../utils/queryCache';

type SaveDebtInput = {
  id?: string;
  data: CreateDebtRequest | UpdateDebtRequest;
};

export const useDebtMutations = () => {
  const queryClient = useQueryClient();

  const invalidateDebts = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['debts'] }),
      queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
      queryClient.invalidateQueries({ queryKey: ['admin'] }),
    ]);
  };

  const saveDebtMutation = useMutation({
    mutationFn: async ({ id, data }: SaveDebtInput) => {
      if (id) return apiClient.updateDebt(id, data as UpdateDebtRequest);
      return apiClient.createDebt(data as CreateDebtRequest);
    },
    onSuccess: invalidateDebts,
  });

  const createPaymentMutation = useMutation({
    mutationFn: (data: CreateDebtRequest) => apiClient.createDebt(data),
    onSuccess: invalidateDebts,
  });

  const deleteDebtMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteDebt(id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ['debts'] });
      const snapshots = snapshotPaginatedQueries<Debt>(queryClient, 'debts');
      updatePaginatedQueries<Debt>(queryClient, 'debts', page => ({
        ...page,
        content: page.content.filter(debt => debt.id !== id),
        totalElements: Math.max(0, page.totalElements - 1),
      }));
      return { snapshots };
    },
    onError: (_error, _id, context: { snapshots: PaginatedSnapshot<Debt> } | undefined) => {
      if (context) restorePaginatedQueries(queryClient, context.snapshots);
    },
    onSettled: invalidateDebts,
  });

  return {
    saveDebtMutation,
    createPaymentMutation,
    deleteDebtMutation,
  };
};
